import { CreditAccountPaymentTypeId } from "../../../features/data-types"
import { TableName } from "../../../tables"
import { toList } from "../../../utils/listUtils"
import { round } from "../../../utils/typeUtils"
import { usePriceOutlineHooks } from "../../operations"
import { SaleOperation, useCustomerLoaders, useHoardLoaders, useSaleLoaders } from "../../sales"
import { useCreditPaymentLoaders } from "../loaders/creditPaymentLoaders"
import { usePaymentTypeLoaders } from "../loaders/paymentTypeLoaders"
import { useTransactionLoaders } from "../loaders/transactionLoaders"
import { CreditPayment, PaymentCategory, PaymentOrderStatus } from "../state/types"
import { CustomerDebtData, DebtData, DebtOperationData } from "./types"

export const useCreditPaymentHooks = () => {
    const { getAdjustedTotal } = usePriceOutlineHooks()
    
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const transactionLoaders = useTransactionLoaders()
    const saleLoaders = useSaleLoaders()
    const hoardLoaders = useHoardLoaders()
    const customerLoaders = useCustomerLoaders()

    const getDebtData = async (
        customerIds: string[],
        dbRequest = false
    ): Promise<DebtData> => {
        const { loadCustomersCreditPayments } = creditPaymentLoaders()
        const loadedCreditPayments = await loadCustomersCreditPayments(customerIds, dbRequest)
        const operationItems = loadedCreditPayments.map(creditPayment => creditPayment.operationItems).flat()
        const { loadCustomersSales } = saleLoaders()
        const loadedSales = await loadCustomersSales(customerIds, dbRequest)
        const creditAccountSales = loadedSales.filter(sale => sale.paymentTypeId === CreditAccountPaymentTypeId && !sale.cancelled)
        const { loadCustomersHoards } = hoardLoaders()
        const loadedHoards = await loadCustomersHoards(customerIds, dbRequest)
        const creditAccountHoards = loadedHoards.filter(hoard => hoard.paymentTypeId === CreditAccountPaymentTypeId && !hoard.cancelled)

        // Paid on the credit account operation: credit payments imputed to it plus payments collected directly on it
        // (e.g. a hoard extension collected at the moment)
        const getPayedAmount = (operation: SaleOperation) => {
            const creditPaymentsAmount = operationItems
                .filter(operationItem => operationItem.operationId === operation.id)
                .reduce((acc, operationItem) => acc + operationItem.amount, 0)
            const directPaymentsAmount = operation.paymentItems.reduce((acc, paymentItem) => acc + paymentItem.amount, 0)
            return creditPaymentsAmount + directPaymentsAmount
        }

        const debtData: DebtData = {}
        customerIds.forEach(customerId => {
            const salesData: DebtOperationData[] = creditAccountSales
                .filter(sale => sale.customerId === customerId)
                .map(sale => {
                    const payedAmount = getPayedAmount(sale)
                    const adjustedTotal = getAdjustedTotal(TableName.SALE_ORDERS, sale, CreditAccountPaymentTypeId)
                    return {
                        table: TableName.SALE_ORDERS,
                        operation: sale,
                        debt: round(adjustedTotal - payedAmount)
                    }
                })
            const hoardsData: DebtOperationData[] = creditAccountHoards
                .filter(hoard => hoard.customerId === customerId)
                .map(hoard => {
                    const payedAmount = getPayedAmount(hoard)
                    const adjustedTotal = getAdjustedTotal(TableName.HOARD_ORDERS, hoard, CreditAccountPaymentTypeId)
                    return {
                        table: TableName.HOARD_ORDERS,
                        operation: hoard,
                        debt: round(adjustedTotal - payedAmount)
                    }
                })
            const operationsData = [...salesData, ...hoardsData]
                .sort((operationData_A, operationData_B) =>
                    operationData_A.operation.creationDate.localeCompare(operationData_B.operation.creationDate)
                )
                .filter(saleData => saleData.debt > 0)
    
            const debt = operationsData.reduce((acc, operationData) => acc + operationData.debt, 0)
            
            debtData[customerId] = {
                operationsData,
                debt
            }
        })

        return debtData
    }

    const getCustomerDebtData = async (
        customerId: string,
        dbRequest = false
    ): Promise<CustomerDebtData> => {
        const debtData = await getDebtData(toList(customerId), dbRequest)
        return debtData[customerId]
    }

    const getOverdueDebtData = async (
        customerId: string,
        dbRequest = false
    ) => {
        const debtData = await getCustomerDebtData(customerId, dbRequest)
        const overdueOperationsData: DebtOperationData[] = []
        await Promise.all(debtData.operationsData.map(async operationData => {
            const dueDate = await getOperationDueDate(operationData.operation)
            if (operationData.debt > 0 && dueDate <= new Date()) {
                overdueOperationsData.push(operationData)
            }
        }))
        const debt = overdueOperationsData.reduce((acc, operationData) => acc + operationData.debt, 0)
        
        return {
            operationsData: overdueOperationsData,
            debt: round(debt)
        }

    }

    // A credit payment accredited at the moment (a check or a payment type without accreditation days) always has a
    // transaction from it: if it doesn't, the accreditation failed and the cash fund movement is missing.
    // Payments with accreditation days are accredited later by a scheduled task, so they can't be checked here
    const isAccreditationMissing = async (creditPayment: CreditPayment, dbRequest = true): Promise<boolean> => {
        const { paymentTypeId, destinationId, status, archived } = creditPayment
        const paymentType = paymentTypeLoaders().findPaymentType(paymentTypeId)
        if (!paymentType || !destinationId || status !== PaymentOrderStatus.PAYED || archived) {
            return false
        }
        const isAccreditedAtTheMoment = paymentType.category === PaymentCategory.CHECK ||
            ([PaymentCategory.CASH_FUND, PaymentCategory.CARD].includes(paymentType.category) && paymentType.accreditationDays === 0)
        if (!isAccreditedAtTheMoment) {
            return false
        }
        const transactions = await transactionLoaders().loadSourceTransactions(creditPayment.id, dbRequest)
        return transactions.length === 0
    }

    const getOperationDueDate = async (operation: SaleOperation) => {
        const dueDate = new Date()
        const creationDate = new Date(operation.creationDate)
        const { loadCustomer } = customerLoaders()
        const customer = await loadCustomer(operation.customerId)
        dueDate.setDate(creationDate.getDate() + (customer!.creditAccount?.creditDays || 1))
        return dueDate
    }
    
    return {
        getCustomerDebtData,
        getOverdueDebtData,
        getOperationDueDate,
        isAccreditationMissing
    }
}

export const creditPaymentHelpers = {
    isArchivable: (creditPayment: CreditPayment): boolean => {
        const { status, archived } = creditPayment
        return status === PaymentOrderStatus.PAYED && !archived
    },
    isAnnullable: (creditPayment: CreditPayment): boolean => {
        const { status, archived } = creditPayment
        return status === PaymentOrderStatus.PAYED && !archived
    }
}
